import type { Trip, TripDay } from "../types";

/**
 * Where a trip goes, day by day.
 *
 * A trip used to be one city (`Trip.city`). Since November 2026 each day can
 * carry its own `city`, so a guest can plan Marrakech for three days, then
 * Essaouira for two, in one trip. The create sheet asks for the route up
 * front as legs (a city and how many days), and any day's city can be changed
 * later (owner, 2026-11-01).
 *
 * `Trip.city` stays and always names the first day's city: the account list
 * (`saved_trips.city`), share links and every older reader keep working. A day
 * without its own `city` is in the trip's city, which is how every trip made
 * before this reads.
 *
 * Pure and framework-free: web and mobile both import it (mobile through
 * `@majourneys/discover/trips`).
 */

/** One stretch of the route: a city and how many days in a row it gets. */
export interface RouteLeg {
  readonly city: string;
  readonly days: number;
}

/** Most cities one route can name when the trip is created. */
export const MAX_ROUTE_LEGS = 6;

/** The city a day is in: its own, else the trip's. */
export function dayCity(trip: Pick<Trip, "city">, day: Pick<TripDay, "city">): string {
  return day.city || trip.city;
}

/** The route as legs: runs of days in the same city, in order. */
export function tripRoute(trip: Pick<Trip, "city" | "days">): RouteLeg[] {
  const legs: { city: string; days: number }[] = [];
  for (const day of trip.days) {
    const city = dayCity(trip, day);
    const last = legs[legs.length - 1];
    if (last && last.city === city) last.days += 1;
    else legs.push({ city, days: 1 });
  }
  if (legs.length === 0 && trip.city) legs.push({ city: trip.city, days: 0 });
  return legs;
}

/** Every city the trip visits, first visit order, each once. */
export function tripCities(trip: Pick<Trip, "city" | "days">): string[] {
  const out: string[] = [];
  if (trip.days.length === 0 && trip.city) return [trip.city];
  for (const day of trip.days) {
    const city = dayCity(trip, day);
    if (city && !out.includes(city)) out.push(city);
  }
  return out;
}

/** True when the trip goes to more than one city. */
export function isMultiCity(trip: Pick<Trip, "city" | "days">): boolean {
  return tripRoute(trip).length > 1;
}

/** Total days a route covers. */
export function routeDayCount(legs: readonly RouteLeg[]): number {
  return legs.reduce((sum, leg) => sum + Math.max(0, leg.days), 0);
}

/**
 * Tidy a route from the create sheet: whole days, at least one per leg, no
 * empty city, and two legs in the same city in a row joined into one.
 */
export function cleanRoute(legs: readonly RouteLeg[]): RouteLeg[] {
  const out: { city: string; days: number }[] = [];
  for (const leg of legs) {
    const city = leg.city?.trim();
    if (!city) continue;
    const days = Math.max(1, Math.round(Number.isFinite(leg.days) ? leg.days : 1));
    const last = out[out.length - 1];
    if (last && last.city === city) last.days += days;
    else out.push({ city, days });
  }
  return out.slice(0, MAX_ROUTE_LEGS);
}

/**
 * The days of a new trip from its route. Every day is given its city, so the
 * trip reads the same even if its first city is later changed.
 */
export function daysFromRoute(
  legs: readonly RouteLeg[],
  newId: () => string,
): TripDay[] {
  const days: TripDay[] = [];
  for (const leg of cleanRoute(legs)) {
    for (let i = 0; i < leg.days; i++) {
      days.push({ id: newId(), label: `Day ${days.length + 1}`, city: leg.city, stops: [] });
    }
  }
  return days;
}

/**
 * Put one day in another city. The trip's own `city` follows day 1, so the
 * account list and older readers always name where the trip starts.
 */
export function setDayCity(trip: Trip, dayId: string, city: string): Trip {
  const slug = city.trim();
  if (!slug || !trip.days.some((d) => d.id === dayId)) return trip;
  // A single-city trip's days have no `city` of their own; give every day its
  // city first, so moving one day does not move the rest with `Trip.city`.
  const days = trip.days.map((d) => ({
    ...d,
    city: d.id === dayId ? slug : dayCity(trip, d),
  }));
  return { ...trip, city: days[0]?.city ?? trip.city, days };
}

/**
 * "Marrakech → Essaouira", from the caller's city names. Pass `rtl` on Arabic
 * pages: the arrow then points the way the line reads.
 */
export function routeLabel(
  trip: Pick<Trip, "city" | "days">,
  cityName: (slug: string) => string,
  rtl = false,
): string {
  return tripRoute(trip)
    .map((leg) => cityName(leg.city))
    .filter((name, i, all) => i === 0 || name !== all[i - 1])
    .join(rtl ? " ← " : " → ");
}

/**
 * Fit a route to a fixed number of days (the dates the guest picked). The
 * last leg takes what is left; if that would leave it with no day, earlier
 * legs give days back from the end, and a route with more legs than days
 * loses its last legs. Without a fixed total the route is returned as is.
 */
export function fitRoute(legs: readonly RouteLeg[], total: number | null): RouteLeg[] {
  const out = legs.map((leg) => ({ city: leg.city, days: Math.max(1, Math.round(leg.days)) }));
  if (total === null || out.length === 0) return out;
  const want = Math.max(1, Math.round(total));
  while (out.length > want) out.pop();
  const last = out.length - 1;
  const others = () => out.slice(0, last).reduce((sum, leg) => sum + leg.days, 0);
  for (let i = last - 1; i >= 0 && want - others() < 1; i--) {
    out[i].days = Math.max(1, out[i].days - (1 - (want - others())));
  }
  out[last].days = want - others();
  return out;
}

/**
 * The next city to suggest when "Add another city" is pressed: the first one
 * in the caller's order that the route does not visit yet, else any city that
 * is not the last leg's.
 */
export function nextRouteCity(legs: readonly RouteLeg[], cities: readonly string[]): string | null {
  const used = new Set(legs.map((leg) => leg.city));
  const last = legs[legs.length - 1]?.city;
  return cities.find((c) => !used.has(c)) ?? cities.find((c) => c !== last) ?? null;
}
